import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedNavBar from '../../components/ui/AuthenticatedNavBar';
import BiasAnalysisForm from './components/BiasAnalysisForm';
import BiasResultsDisplay from './components/BiasResultsDisplay';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import UpgradeModal from '../../components/ui/UpgradeModal';

const BiasDetection = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const API_URL = import.meta.env?.VITE_SERVER_URL || 'http://localhost:4000';

  useEffect(() => {
    const loadUser = async () => {
      // Local first
      const stored = localStorage.getItem('user');
      if (stored) {
        try { setUser(JSON.parse(stored)); } catch {}
      }
      // Refresh from server
      try {
        const res = await fetch(`${API_URL}/api/user/data`, { credentials: 'include' });
        const data = await res.json();
        if (data?.success) {
          const merged = {
            ...JSON.parse(localStorage.getItem('user') || '{}'),
            name: data?.userData?.name,
            email: data?.userData?.email,
            isVerified: data?.userData?.isAccountVerified ?? false
          };
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        }
      } catch (_) {}
    };
    loadUser();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem('ddf_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };

  const simulateBiasAnalysis = async (analysisData) => {
    const isUrl = analysisData?.type === 'url' || /^https?:\/\//i.test(analysisData?.content || '');
    const payload = isUrl
      ? { url: analysisData?.content, query_hint: analysisData?.topic || '' }
      : { text: analysisData?.content, query_hint: analysisData?.topic || '' };

    const res = await fetch(`${API_URL}/api/agents/bias`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    // Handle usage limit exceeded
    if (res.status === 429) {
      const errorData = await res.json();
      if (errorData?.upgradeRequired) {
        throw new Error('USAGE_LIMIT_EXCEEDED');
      }
    }
    
    if (!res.ok) {
      throw new Error(`Request failed: ${res.status}`);
    }
    
    const api = await res.json();

    // Map Python API -> existing UI shape
    const overallScore = Number(api?.overall_score ?? 0);
    const level = overallScore <= 30 ? 'Low' : overallScore <= 60 ? 'Medium' : 'High';

    return {
      overallBias: { level, score: overallScore },
      biasTypes: [
        { type: 'Political', severity: (api?.labels?.ideology?.label || '').toLowerCase() || 'medium', description: api?.labels?.ideology?.rationale || '' },
        { type: 'Linguistic', severity: (api?.labels?.framing?.label || '').toLowerCase() || 'medium', description: api?.labels?.framing?.rationale || '' },
        { type: 'Source', severity: (api?.labels?.source_bias?.label || '').toLowerCase() || 'low', description: api?.labels?.source_bias?.rationale || '' }
      ].filter(x => x.description),
      keyFindings: (api?.key_phrases || []).slice(0, 6),
      evidence: (api?.claims || []).slice(0, 4).map(c => ({ text: c?.claim, explanation: (c?.support || 'unknown') })),
      recommendations: [
        api?.labels?.evidence_quality?.rationale ? `Evidence quality: ${api?.labels?.evidence_quality?.rationale}` : 'Improve evidence quality and cite reputable sources.',
        'Balance perspectives and reduce sensational language where possible.'
      ]
    };
  };

  const handleAnalyze = async (analysisData) => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      // Simulate API call to /bias endpoint
      const analysisResults = await simulateBiasAnalysis(analysisData);
      setResults(analysisResults);
    } catch (error) {
      console.error('Bias analysis failed:', error);
      if (error.message === 'USAGE_LIMIT_EXCEEDED') {
        setShowUpgradeModal(true);
        setError('Usage limit reached. Upgrade to Pro or wait for your monthly reset.');
      } else {
        setError('Failed to analyze content for bias. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResults = async (content) => {
    try {
      await navigator.clipboard?.writeText(content);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Failed to copy results:', error);
      setError('Failed to copy results to clipboard');
    }
  };

  const handleNewAnalysis = () => {
    setResults(null);
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>Bias Detection - DDF Agent</title>
        <meta name="description" content="Analyze content for potential bias patterns and receive actionable improvement recommendations with AI-powered detection algorithms." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <AuthenticatedNavBar user={user} onLogout={handleLogout} />
        
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Success Message */}
            {showSuccessMessage && (
              <div className="fixed top-20 right-4 z-50 bg-success text-success-foreground px-4 py-3 rounded-lg shadow-elevated flex items-center space-x-2 animate-in slide-in-from-right-4 duration-300">
                <Icon name="Check" size={16} />
                <span className="text-sm font-medium">Results copied to clipboard!</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <div>
                  <p className="text-sm text-error font-medium">{error}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="mt-2 text-error hover:text-error/80"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}

            {/* Analysis Form */}
            <BiasAnalysisForm 
              onAnalyze={handleAnalyze}
              loading={loading}
            />

            {/* Results Display */}
            <BiasResultsDisplay
              results={results}
              onCopyResults={handleCopyResults}
              onNewAnalysis={handleNewAnalysis}
            />

            {/* Help Section */}
            {!results && !loading && (
              <div className="mt-12 bg-card rounded-xl border border-border p-8">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon name="HelpCircle" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    How Bias Detection Works
                  </h3>
                  <p className="text-muted-foreground">
                    Our AI-powered system analyzes content for various types of bias and provides actionable feedback
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground flex items-center space-x-2">
                      <Icon name="Target" size={16} color="var(--color-primary)" />
                      <span>What We Detect</span>
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center space-x-2">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                        <span>Political and ideological bias</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                        <span>Cultural and social bias</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                        <span>Linguistic and emotional bias</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                        <span>Gender and demographic bias</span>
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground flex items-center space-x-2">
                      <Icon name="Lightbulb" size={16} color="var(--color-primary)" />
                      <span>What You Get</span>
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center space-x-2">
                        <Icon name="BarChart3" size={14} color="var(--color-accent)" />
                        <span>Detailed bias severity scores</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="FileText" size={14} color="var(--color-accent)" />
                        <span>Specific examples and evidence</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={14} color="var(--color-accent)" />
                        <span>Actionable improvement recommendations</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Icon name="Copy" size={14} color="var(--color-accent)" />
                        <span>Shareable analysis reports</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
        
        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="bias detection"
          usageInfo={{
            usage: 10,
            limit: 10
          }}
        />
      </div>
    </>
  );
};

export default BiasDetection;